import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { SessionStorageService } from './session-storage.service';
import { HttpService } from './http.service';
import { APINoDataResponse } from '../interfaces';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  GoogleAuthProvider,
  PhoneAuthProvider,
  RecaptchaVerifier,
  getAuth,
  OAuthProvider,
  User,
  linkWithCredential,
  signInWithPhoneNumber,
  AuthCredential,
  EmailAuthProvider } from 'firebase/auth';


@Injectable({
  providedIn: 'root'
})

export class AuthService {

  reCaptchaVerifier: RecaptchaVerifier | undefined = undefined;
  private readonly recaptchaVerifierSubject = new BehaviorSubject<RecaptchaVerifier | null>(null);
  private readonly inscriptionCompleted$ = new Subject<boolean>();
  inscriptionCompleted = this.inscriptionCompleted$.asObservable();

  constructor(
    private auth: AngularFireAuth,
    private http: HttpService,
    public router: Router,
    private sessionStorageService: SessionStorageService
  ) {}

  getRecaptchaVerifier() {
    let authInstance = getAuth();
    if (this.reCaptchaVerifier === undefined) {
      this.reCaptchaVerifier = new RecaptchaVerifier('sign-in-button', {'size': 'invisible'}, authInstance);
    }
    return this.reCaptchaVerifier;
  }

  clearRecaptchaVerifier() {
    if (this.reCaptchaVerifier) {
      this.reCaptchaVerifier.clear();
      this.reCaptchaVerifier = undefined;
      this.recaptchaVerifierSubject.next(null);
    }
  }

  loginInApp(user: firebase.default.User){
    return user.getIdToken(false);
  }

  loginWithEmailAndPassword(email: string, password: string){
    try {
      return this.auth.signInWithEmailAndPassword(email, password);
    }
    catch(err) {
      console.log("Login error: ", err)
      return null;
    }
  }

  getOTP(phoneNumber: string) {
    this.clearRecaptchaVerifier();
    const authInstance = getAuth();
    const verifier = this.getRecaptchaVerifier();
    return signInWithPhoneNumber(authInstance, phoneNumber, verifier).catch(error => {
      console.error('Error during phone sign-in:', error);
      this.clearRecaptchaVerifier();
      return Promise.reject(error);
    });
  }

  loginWithPhone(verificationId: string, verificationCode: string) {
    var credentials = PhoneAuthProvider.credential(verificationId, verificationCode);
    return this.auth.signInWithCredential(credentials);
  }

  addPhoneToUser(verificationId: string, verificationCode: string) {
    var credentials = PhoneAuthProvider.credential(verificationId, verificationCode);
    return this.auth.currentUser.then(user => user!.updatePhoneNumber(credentials));
  }

  loginWithCustomToken(token: string) {
    return this.auth.signInWithCustomToken(token);
  }

  getEmailProvider(providerId: string, email?: string) {
    let provider;
    switch(providerId) {
      case 'password':
        return null;
      case 'google.com':
        provider = new GoogleAuthProvider();
        break;
      case 'microsoft.com':
        provider = new OAuthProvider('microsoft.com');
        provider.setCustomParameters({
          TENANT_ID: '0bf7af2d-2da9-47c0-84bf-bd76abe61c00'
        })
        break;
      default:
        throw new Error('Provider not found');
    }
    if (email) {
      provider.setCustomParameters({ login_hint: email });
    }
    return provider;
  }

  loginWithEmailProvider(providerId: string, email?: string) {
    const provider = this.getEmailProvider(providerId, email);
    if (provider === null) {
      this.router.navigate(['/login']);
      return null
    }
    else {
      return this.auth.signInWithPopup(provider);
    }
  }

  async linkEmailPassword(user: firebase.default.User, email: string, password: string, name: string | null = null): Promise<void> {
    const credential = EmailAuthProvider.credential(email, password);
    if (name) {
      await user.updateProfile({ displayName: name });
    }
    try {
      await linkWithCredential(user as unknown as User, credential);
    } catch (error: any) {
      // If a previous (partially-failed) attempt already linked the password
      // provider to this user, a retry re-runs this step and Firebase rejects it
      // with 'provider-already-linked'. That's the desired end state, so treat it
      // as success and let provisioning continue. Other errors (e.g. the email
      // belongs to a different account) still propagate.
      if (error?.code === 'auth/provider-already-linked') {
        return;
      }
      throw error;
    }
  }

  async linkProviderToExistingAccount(pendingCredential: AuthCredential, existingUser: User) {
    try {
      const result = await linkWithCredential(existingUser, pendingCredential);
      return result;
    } catch (error) {
      console.error('Error al vincular proveedor:', error);
      throw error;
    }
  }

  fetchSignInMethodsForEmail(token: string, action: string, email: string): Promise<string[]> {
    return this.http.userExistEmail(token, action, email)
    .then(() => {
      return this.auth.fetchSignInMethodsForEmail(email).then((providers: string[]) => {
        if (providers.length === 0) {
          return ['phone'];
        }
        else {
          return providers;
        }
      });
    })
    .catch((error: HttpErrorResponse) => {
      if (error.status === 404) {
        return [];
      }
      throw error;
    })
  }

  sendEmailVerification(isNewUser: boolean=true): Promise<APINoDataResponse> {
    return this.auth.currentUser!.then(user => user!.email)
    .then(email => this.http.getVerificationEmail(email, isNewUser));
  }

  sendBindEmail(email: string): Promise<APINoDataResponse> {
    return this.http.getBindEmail(email);
  }

  verifyEmail(token: string, isNewUser: boolean): Promise<any> {
    return this.http.verifyEmail(token, isNewUser);
  }

  bindEmail(token: string): Promise<any> {
    return this.http.bindEmail(token).then((response) => {
      this.loginWithCustomToken(response.data.customToken);
    })
  }

  updatePassword(email: string, password: string, newPassword: string){
    return this.loginWithEmailAndPassword(email, password)!.then(() => this.auth.user.subscribe(u => u!.updatePassword(newPassword)));
  }


  recoverPassword(email: string){
    return this.auth.sendPasswordResetEmail(email);
  }


  getUser() {
    return this.auth.user;
  }

  refreshUser(): Promise<void> {
    return this.auth.currentUser.then(user => user!.reload());
  }

  async logout(storeId: string = ''){
    this.auth.signOut().then(() => {
      this.sessionStorageService.clearUser();
      this.router.navigate(['login'], { queryParams: { storeId: storeId } });
    })
  }

  async signOutOnly(): Promise<void> {
    await this.auth.signOut();
    this.sessionStorageService.clearUser();
  }

  getUsersIdTokenResult() {
    return this.auth.idTokenResult;
  }

  async isUserFullyRegistered(): Promise<boolean> {
    let authUser = await this.auth.currentUser;
    return !!authUser && authUser.emailVerified && authUser.phoneNumber !== null;
  }

  notifyInscriptionCompleted() {
    this.inscriptionCompleted$.next(true);
  }
}
